import { isNil } from 'lodash'
import { cn } from '~/@/lib/utils'
import {
  BreadcrumbObjectType,
  useGetDetailedChartQuery,
  useGetSettingsPageTableDetailsQuery,
  useGetTableRowQuery,
} from '~/common/generated/apollo-operations'
import { BackLinkProvider } from '../../common/hooks/useBackLink'
import { useGlobalContext } from '../../common/hooks/useGlobalContext'
import { usePage } from '../../common/hooks/usePage'
import { DetailedChart } from '../charts/types'
import { RecordDetailTableRow } from '../records/types'
import { SettingsPageTable } from '../tables/types'
import { TitleBarActions } from './TitleBarActions'
import { TitleBarHeader } from './TitleBarHeader'
import { TitleBarNav } from './TitleBarNav'

// This is used in a few legacy components and will be removed when they are replaced
export const PAGE_NAVIGATION_HEIGHT = 56

export type TitleBarProps = {
  isLeftNavExpanded?: boolean
  isLeftNavPeeking?: boolean
  showBackLink?: boolean
  sharedObject?: {
    objectName: string
    workspaceType?: BreadcrumbObjectType
  }
  tableRow?: RecordDetailTableRow | null
  tableData?: SettingsPageTable | null
  chart?: DetailedChart | null
  isUnauthenticatedSharedRoute: boolean
  onLeftNavPeekStart?: () => void
  onLeftNavPeekEnd?: () => void
}

// This component is used to display the page navigation such as breadcrumbs and actions for the page
export function TitleBar({
  isLeftNavExpanded,
  isLeftNavPeeking,
  showBackLink,
  sharedObject,
  isUnauthenticatedSharedRoute,
  onLeftNavPeekStart,
  onLeftNavPeekEnd,
}: TitleBarProps) {
  const { user } = useGlobalContext()
  const { objectId, objectType, pageType } = usePage()
  const { data: tableRowData } = useGetTableRowQuery({
    variables: {
      tableRowId: objectId || '',
    },
    skip: !objectId || objectType !== BreadcrumbObjectType.TABLE_ROW || !isNil(sharedObject),
  })
  const { data: tableData } = useGetSettingsPageTableDetailsQuery({
    variables: {
      tableId: objectId || '',
    },
    skip: !objectId || objectType !== BreadcrumbObjectType.TABLE || !isNil(sharedObject),
  })
  const { data: chartData } = useGetDetailedChartQuery({
    variables: { chartId: objectId ?? '' },
    skip:
      !objectId ||
      (objectType !== BreadcrumbObjectType.CHART &&
        objectType !== BreadcrumbObjectType.PIVOT_TABLE),
  })

  if (!user && !isUnauthenticatedSharedRoute) {
    return null
  }

  return (
    <div
      className={`title-bar group relative z-[1201] flex h-[52px] w-full items-center justify-between border-b border-gray-4 bg-white`}
    >
      {/* On form settings page, apply flex-1 to balance with right section, centering the middle nav */}
      <div
        className={cn(
          'flex flex-row items-center gap-6 ps-3 md:ps-6',
          pageType === 'formSettings' ? 'flex-1' : '',
        )}
      >
        <BackLinkProvider>
          <TitleBarHeader
            chart={chartData?.chart}
            isLeftNavExpanded={isLeftNavExpanded}
            isLeftNavPeeking={isLeftNavPeeking}
            isUnauthenticatedSharedRoute={isUnauthenticatedSharedRoute}
            onLeftNavPeekEnd={onLeftNavPeekEnd}
            onLeftNavPeekStart={onLeftNavPeekStart}
            sharedObject={sharedObject}
            showBackLink={showBackLink}
          />
        </BackLinkProvider>
      </div>
      <div className="flex h-full justify-center">
        <TitleBarNav />
      </div>
      <div className="flex flex-1 justify-end pe-6">
        <TitleBarActions
          chart={chartData?.chart}
          isUnauthenticatedSharedRoute={isUnauthenticatedSharedRoute}
          sharedObject={sharedObject}
          tableData={tableData?.table}
          tableRow={tableRowData?.tableRow}
        />
      </div>
    </div>
  )
}
